import { conversationStudy, viewportContextOf } from '../context';
import { askErrorText } from '../panel';
import { askText } from './ask-text';
import { el, type ToolDeps } from './dom';
import { MARK_COLORS, removeMark, setMark } from './marks';
import { pickBox } from './picker';

/**
 * "Preguntar por área": the radiologist drags a box on the active image (no annotation is created),
 * picks or types a question, and ris answers about that crop (mode `region`). The answer joins the
 * conversation like any other, its findings reviewable and drawn on the full image.
 */

export const REGION_TEXT = {
  button: 'Preguntar por área',
  title: 'Preguntar por el área marcada',
  label: 'Pregunta sobre el área',
  hint: 'El Asistente Guru analizará solo el recuadro marcado, ampliado.',
  confirm: 'Preguntar',
  tooShort: 'Escriba una pregunta.',
  noImage: 'Muestre una imagen del estudio en el visor activo para marcar un área.',
  mark: 'Área consultada',
  thinking: 'pensando…',
} as const;

export const REGION_QUESTIONS = [
  '¿Qué ves en esta área?',
  '¿Hay alguna lesión en esta área?',
  'Describe la densidad o intensidad de esta área.',
] as const;

const MARK_ID = 'region-question';
const MIN_QUESTION = 2;
const MAX_QUESTION = 1000;

function question(deps: ToolDeps): Promise<string | null> {
  return askText(deps.shell, {
    title: REGION_TEXT.title,
    label: REGION_TEXT.label,
    hint: REGION_TEXT.hint,
    className: 'assistant-region-dialog',
    confirm: REGION_TEXT.confirm,
    initial: REGION_QUESTIONS[0],
    suggestions: REGION_QUESTIONS,
    minLength: MIN_QUESTION,
    maxLength: MAX_QUESTION,
    tooShort: REGION_TEXT.tooShort,
  });
}

export async function askRegion(deps: ToolDeps): Promise<void> {
  const { shell, panel, api } = deps;
  const study = conversationStudy(shell);
  const picked = await pickBox(shell);
  if (!picked) return;
  const viewport = viewportContextOf(picked.target.viewport, study);
  if (!viewport) return void shell.toast(REGION_TEXT.noImage, 'warning');
  setMark({ id: MARK_ID, instanceUid: viewport.instance_uid, frame: viewport.frame, box: picked.box, label: REGION_TEXT.mark, color: MARK_COLORS.region });
  const message = await question(deps);
  if (message === null) return void removeMark(MARK_ID);
  const thinking = el('div', 'assistant-thinking', REGION_TEXT.thinking);
  thinking.setAttribute('role', 'status');
  // A card of this study's conversation: it stays across history reloads until the answer lands.
  panel.appendResult(thinking, study);
  try {
    const answer = await api.askRegion(study, { message, viewport, box: picked.box });
    // ris stored the answer in this study's history: shown as conversation messages (only in this
    // study's conversation), a history reload replaces them with the stored copies.
    panel.appendMessages(answer, study);
  } catch (error) {
    console.warn('[viewer] region question failed', error);
    shell.toast(askErrorText(error), 'error');
  } finally {
    panel.removeResult(thinking);
    removeMark(MARK_ID);
  }
}
